import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Paper,
  Divider,
  InputAdornment,
  Chip,
  Switch,
  Stack,
} from "@mui/material";
import {
  numberToWords,
  formatIndianCurrency,
  parseCurrency,
} from "../../../utils/numberToWords";

const GSTCalculator = () => {
  const [amount, setAmount] = useState(0);
  const [amountDisplay, setAmountDisplay] = useState("0");
  const [gstType, setGstType] = useState("exclusive");
  const [gstRate, setGstRate] = useState("18");
  const [isInterState, setIsInterState] = useState(false); // false = CGST+SGST, true = IGST

  const [calculated, setCalculated] = useState({
    gstAmount: 0,
    totalAmount: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
  });

  useEffect(() => {
    const amt = parseFloat(amount);
    const rate = parseFloat(gstRate);

    if (isNaN(amt) || amt <= 0 || isNaN(rate) || rate < 0) {
      setCalculated({
        gstAmount: 0,
        totalAmount: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
      });
      return;
    }

    let gstAmount, totalAmount;
    if (gstType === "exclusive") {
      gstAmount = (amt * rate) / 100;
      totalAmount = amt + gstAmount;
    } else {
      gstAmount = (amt * rate) / (100 + rate);
      totalAmount = amt;
    }

    setCalculated({
      gstAmount: gstAmount,
      totalAmount: totalAmount,
      cgst: isInterState ? 0 : gstAmount / 2,
      sgst: isInterState ? 0 : gstAmount / 2,
      igst: isInterState ? gstAmount : 0,
    });
  }, [amount, gstType, gstRate, isInterState]);

  const handleAmountChange = (val) => {
    const num = parseCurrency(val);
    setAmount(num);
    setAmountDisplay(formatIndianCurrency(num));
  };

  return (
    <Box>
      <Grid container spacing={6}>
        <Grid item xs={12} md={7}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {/* Amount Input */}
            <Box>
              <FormLabel
                sx={{
                  fontWeight: 700,
                  color: "#333",
                  mb: 2,
                  display: "block",
                  fontSize: "0.9rem",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Base Amount
              </FormLabel>
              <TextField
                fullWidth
                value={amountDisplay}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder="0"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  sx: { borderRadius: "12px", bgcolor: "#fcfcfc", py: 0.5 },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "var(--primary-color)",
                  fontWeight: 600,
                  mt: 1,
                  display: "block",
                }}
              >
                {numberToWords(amount)}
              </Typography>
            </Box>

            <Divider />

            {/* GST Type Selection */}
            <FormControl component="fieldset">
              <FormLabel
                sx={{
                  fontWeight: 700,
                  color: "#333",
                  mb: 1,
                  fontSize: "0.9rem",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Calculation Logic
              </FormLabel>
              <RadioGroup
                row
                value={gstType}
                onChange={(e) => setGstType(e.target.value)}
              >
                <FormControlLabel
                  value="exclusive"
                  control={
                    <Radio
                      sx={{
                        "&.Mui-checked": { color: "var(--primary-color)" },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>
                        GST Exclusive
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#777" }}>
                        Add tax on top of amount
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="inclusive"
                  control={
                    <Radio
                      sx={{
                        "&.Mui-checked": { color: "var(--primary-color)" },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>
                        GST Inclusive
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#777" }}>
                        Tax is already in the amount
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            <Divider />

            {/* Inter-state vs Intra-state Switch */}
            <Box>
              <FormLabel
                sx={{
                  fontWeight: 700,
                  color: "#333",
                  mb: 2,
                  display: "block",
                  fontSize: "0.9rem",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Transaction Type
              </FormLabel>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: !isInterState ? "var(--primary-color)" : "#777",
                  }}
                >
                  Intra-state (CGST+SGST)
                </Typography>
                <Switch
                  checked={isInterState}
                  onChange={(e) => setIsInterState(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "var(--primary-color)",
                      "& + .MuiSwitch-track": {
                        backgroundColor: "var(--primary-color)",
                      },
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: isInterState ? "var(--primary-color)" : "#777",
                  }}
                >
                  Inter-state (IGST)
                </Typography>
              </Stack>
            </Box>

            <Divider />

            {/* GST Rate Selection */}
            <Box>
              <FormLabel
                sx={{
                  fontWeight: 700,
                  color: "#333",
                  mb: 2,
                  display: "block",
                  fontSize: "0.9rem",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Tax Slab (GST %)
              </FormLabel>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                {[0, 5, 12, 18, 28, 40].map((rate) => (
                  <Chip
                    key={rate}
                    label={`${rate}%`}
                    onClick={() => setGstRate(rate.toString())}
                    sx={{
                      width: "80px",
                      height: "45px",
                      fontSize: "1rem",
                      fontWeight: 700,
                      borderRadius: "12px",
                      bgcolor:
                        gstRate === rate.toString()
                          ? "var(--primary-color)"
                          : "#f0f0f0",
                      color: gstRate === rate.toString() ? "#fff" : "#333",
                      "&:hover": {
                        bgcolor:
                          gstRate === rate.toString()
                            ? "var(--primary-color)"
                            : "#e0e0e0",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              height: "100%",
              borderRadius: "24px",
              bgcolor: "var(--primary-color)",
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              boxShadow: "0 20px 40px rgba(26, 66, 161, 0.2)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="overline"
                sx={{ opacity: 0.8, letterSpacing: 2 }}
              >
                Final Total Amount
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  my: 2,
                  fontFamily: "'Roboto Slab', serif",
                  fontSize: { xs: "2.5rem", md: "3rem" },
                  color: "var(--tertiary-color)",
                }}
              >
                ₹
                {calculated.totalAmount.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "var(--tertiary-color)",
                  opacity: 0.9,
                  fontWeight: 600,
                  display: "block",
                  mb: 2,
                }}
              >
                {numberToWords(calculated.totalAmount)}
              </Typography>

              <Box
                sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 2 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    p: 2,
                    bgcolor: "rgba(255,255,255,0.08)",
                    borderRadius: "12px",
                  }}
                >
                  <Typography
                    sx={{ color: "var(--tertiary-color)" }}
                    variant="body2"
                  >
                    Net GST (Total)
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 700, color: "var(--tertiary-color)" }}
                  >
                    ₹
                    {calculated.gstAmount.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>

                {!isInterState ? (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        px: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ opacity: 0.7, color: "var(--tertiary-color)" }}
                      >
                        CGST ({(parseFloat(gstRate) || 0) / 2}%)
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "var(--tertiary-color)" }}
                      >
                        ₹
                        {calculated.cgst.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        px: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ opacity: 0.7, color: "var(--tertiary-color)" }}
                      >
                        SGST ({(parseFloat(gstRate) || 0) / 2}%)
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "var(--tertiary-color)" }}
                      >
                        ₹
                        {calculated.sgst.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      px: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.7, color: "var(--tertiary-color)" }}
                    >
                      IGST ({parseFloat(gstRate) || 0}%)
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "var(--tertiary-color)" }}
                    >
                      ₹
                      {calculated.igst.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  mt: 4,
                  pt: 3,
                  borderTop: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.7, fontStyle: "italic" }}
                >
                  Calculation Type:{" "}
                  {gstType === "exclusive"
                    ? "Tax added to base"
                    : "Tax extracted from base"}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GSTCalculator;
